import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Phone, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [next, setNext] = useState('/login');
  const { login } = useAuth();
  const started = useRef(false);
  
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided');
      return;
    }
    
    // Verify the email, then sign the user straight in
    axios.get(`${API}/auth/verify-email?token=${token}`)
      .then(res => {
        if (res.data.token && res.data.user) {
          login(res.data.user, res.data.token);
          setNext(res.data.user.business_id ? `/business/${res.data.user.business_id}` : res.data.user.role === 'sales_rep' ? '/sales' : '/pricing');
        }
        setStatus('success');
        setMessage(res.data.token ? "You're all set. You're now signed in." : 'Your email is already verified. Please sign in.');
      })
      .catch(err => {
        setStatus('error');
        const d = err.response?.data?.detail;
        setMessage(typeof d === 'string' ? d : 'Verification failed. The link may be expired.');
      });
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center">
        {/* Logo */}
        <Link to="/" className="inline-flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center">
            <Phone className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-bold text-foreground">
            Front<span className="text-primary">DeskPro</span>
          </span>
        </Link>
        
        {status === 'verifying' && (
          <div className="py-8">
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Verifying your email...</h2>
            <p className="text-muted-foreground">Please wait a moment</p>
          </div>
        )}
        
        {status === 'success' && (
          <div className="py-8">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Email Verified!</h2>
            <p className="text-muted-foreground mb-6">{message}</p>
            <Button 
              onClick={() => navigate(next)}
              className="bg-primary hover:bg-primary/90"
              data-testid="verify-email-continue-btn"
            >
              {next === '/login' ? 'Go to Login' : 'Continue'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
        
        {status === 'error' && (
          <div className="py-8">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Verification Failed</h2>
            <p className="text-muted-foreground mb-6">{message}</p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-primary hover:bg-primary/90"
              >
                Go to Login
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/signup')}
                className="w-full"
              >
                Sign Up Again
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default VerifyEmail;
